import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Store, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import DealCard from "../components/DealCard";

export default function Follows() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyMerchant, setBusyMerchant] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/deals/following");
        setMerchants(data.followed_merchants || []);
        setDeals(data.deals || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, navigate]);

  const unfollow = async (name) => {
    setBusyMerchant(name);
    try {
      await api.post(`/merchants/${encodeURIComponent(name)}/follow`);
      setMerchants((m) => m.filter((x) => x !== name));
      setDeals((d) => d.filter((deal) => (deal.merchant || "").toLowerCase() !== name.toLowerCase()));
      toast.success(`Unfollowed ${name}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyMerchant(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="follows-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-[#E63946]"
        data-testid="follows-back-link"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
        Back to home
      </Link>

      <div className="mt-6 mb-10 border-b border-black/10 pb-8">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2 inline-flex items-center gap-1.5">
          <Heart className="w-3 h-3" strokeWidth={2.5} fill="currentColor" />
          My Follows
        </div>
        <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter">
          Fresh deals from your brands
        </h1>
        <p className="mt-3 text-black/60 font-serif italic max-w-2xl">
          Every active deal from the merchants you follow, sorted by newest first.
          Get notified the moment a new drop goes live.
        </p>
      </div>

      {/* Followed merchants row */}
      {merchants.length > 0 && (
        <div className="mb-10" data-testid="follows-merchant-list">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 mb-3">
            Following {merchants.length} {merchants.length === 1 ? "merchant" : "merchants"}
          </div>
          <div className="flex flex-wrap gap-2">
            {merchants.map((m) => (
              <div key={m} className="inline-flex items-center gap-2 border border-black/20 pl-3 pr-1 py-1 bg-white group" data-testid={`follow-chip-${m}`}>
                <Link to={`/merchant/${encodeURIComponent(m)}`} className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest hover:text-[#E63946]">
                  <Store className="w-3 h-3" strokeWidth={2} />
                  {m}
                </Link>
                <button
                  type="button"
                  onClick={() => unfollow(m)}
                  disabled={busyMerchant === m}
                  className="text-[10px] font-bold uppercase tracking-widest text-black/40 hover:text-[#E63946] px-2 py-1 border-l border-black/10 disabled:opacity-40"
                  data-testid={`unfollow-btn-${m}`}
                  title={`Unfollow ${m}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deals grid */}
      {loading ? (
        <div className="text-black/40 font-mono uppercase tracking-widest text-xs">Loading feed…</div>
      ) : deals.length === 0 && merchants.length === 0 ? (
        <div className="border border-dashed border-black/20 p-12 text-center bg-white" data-testid="follows-empty">
          <div className="font-serif text-3xl mb-3">You&rsquo;re not following any merchants yet.</div>
          <p className="text-black/60 text-sm mb-6 max-w-md mx-auto">
            Visit any merchant page and tap <span className="font-bold">Follow</span> to have their latest deals delivered right here.
          </p>
          <Link to="/" className="inline-block bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-5 py-3 hover:bg-[#E63946]" data-testid="follows-browse-btn">
            Browse merchants
          </Link>
        </div>
      ) : deals.length === 0 ? (
        <div className="border border-dashed border-black/20 p-12 text-center bg-white" data-testid="follows-empty-deals">
          <div className="font-serif text-2xl mb-2">No live deals right now.</div>
          <p className="text-black/60 text-sm">We&rsquo;ll email you the moment your brands drop something new.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="follows-deals-grid">
          {deals.map((d) => (<DealCard key={d.id} deal={d} />))}
        </div>
      )}
    </div>
  );
}
